import {
    faChevronsLeft,
    faChevronsRight,
} from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useMemo } from "react";
import { DateRangeField } from "../../../../ui/Inputs";
import { useMapData, useMapFilters } from "../../hooks/mapDataAndFilters";
import { MenuCategory } from "../../ui/Menu";
import { ControlContainer } from "../common";

export const DateRangeControl = () => {
    const { filterState, updateMapFilterState } = useMapFilters("mainMap");
    const { selectedContext, setSelectedContext } = useMapData("mainMap");

    /**
     * Memoize dates from state to avoid unnecessary rerenders.
     */
    const startDateFilter = useMemo(
        () => filterState.emissions.startDateFilter,
        [filterState.emissions.startDateFilter],
    );
    const endDateFilter = useMemo(
        () => filterState.emissions.endDateFilter,
        [filterState.emissions.endDateFilter],
    );

    /**
     * Handlers for changing start and end dates
     */
    const handleChangeStartDate = useCallback(
        (v) => {
            // The map date range must always be defined, so ignore empty/invalid
            // values and keep the last valid date instead of clearing the filter.
            if (!v) return;
            updateMapFilterState((d) => {
                d.emissions.startDateFilter = v;
            });
            setSelectedContext({
                ...selectedContext,
                dateRangeToShowData: undefined,
            });
        },
        [updateMapFilterState, selectedContext, setSelectedContext],
    );
    const handleChangeEndDate = useCallback(
        (v) => {
            // The map date range must always be defined, so ignore empty/invalid
            // values and keep the last valid date instead of clearing the filter.
            if (!v) return;
            updateMapFilterState((d) => {
                d.emissions.endDateFilter = v;
            });
            setSelectedContext({
                ...selectedContext,
                dateRangeToShowData: undefined,
            });
        },
        [updateMapFilterState, selectedContext, setSelectedContext],
    );

    /**
     * Compute number of approximate months between the two dates.
     *
     * This is used to highlight the range shortcut buttons at the
     * top, even if the dates are a little bit off.
     */
    const monthsBetweenDates = useMemo(() => {
        const d1 = new Date(filterState.emissions.startDateFilter).getTime();
        const d2 = new Date(filterState.emissions.endDateFilter).getTime();

        // Check if range is invalid (From date is after To date)
        const isInvalid = d1 > d2;
        if (isInvalid) {
            return 0;
        }

        // Calculate the difference in milliseconds
        const difference = Math.abs(d2 - d1);

        // Convert the difference to months
        const months = difference / (1000 * 60 * 60 * 24 * 30);

        // Round to the nearest integer and return
        return Math.round(months);
    }, [
        filterState.emissions.startDateFilter,
        filterState.emissions.endDateFilter,
    ]);

    /**
     * Update date ranges (used by presets).
     */
    const updateRange = (months: number) => {
        updateMapFilterState((d) => {
            d.emissions.startDateFilter = new Date(
                new Date().setMonth(new Date().getMonth() - months),
            ).toISOString();
            d.emissions.endDateFilter = new Date().toISOString();
        });
        setSelectedContext({
            ...selectedContext,
            dateRangeToShowData: undefined,
        });
    };

    /**
     * Used by the << and >> buttons to move back and forward
     * between time periods.
     *
     * Eg: operators select a range, like 1 year and then navigate
     * "in time" in 1 year slices of data.
     */
    const moveNPeriods = (periodsToMove: number) => {
        const d1 = new Date(filterState.emissions.startDateFilter).getTime();
        const d2 = new Date(filterState.emissions.endDateFilter).getTime();
        const difference = Math.abs(d2 - d1);
        updateMapFilterState((d) => {
            d.emissions.startDateFilter = new Date(
                d1 + periodsToMove * difference,
            ).toISOString();
            d.emissions.endDateFilter = new Date(
                d2 + periodsToMove * difference,
            ).toISOString();
        });
    };

    return (
        <ControlContainer className="p-0.5 w-fit">
            <div className="bg-white p-1 rounded text-sm w-[400px]">
                <div className="flex w-full items-center justify-between mb-1">
                    <MenuCategory>Filter by date range</MenuCategory>
                    <div className="flex items-center gap-2">
                        {[
                            ["3m", 3],
                            ["6m", 6],
                            ["1y", 12],
                            ["3y", 12 * 3],
                            ["5y", 12 * 5],
                        ].map((r: [string, number]) => (
                            <button
                                key={r[0]}
                                type="button"
                                onClick={() => updateRange(r[1])}
                                className={`
                                    flex items-center gap-1 text-ae-blue-550 text-xs
                                    hover:bg-ae-blue-50 p-1 rounded
                                    ${monthsBetweenDates === r[1] ? "bg-ae-blue-50" : ""}
                                `}
                            >
                                {r[0]}
                            </button>
                        ))}
                        <button
                            type="button"
                            onClick={() => moveNPeriods(-1)}
                            className="flex items-center gap-1 text-ae-blue-550 text-xs hover:bg-ae-blue-50 p-1 rounded"
                        >
                            <FontAwesomeIcon icon={faChevronsLeft} />
                        </button>
                        <button
                            type="button"
                            onClick={() => moveNPeriods(1)}
                            className="flex items-center gap-1 text-ae-blue-550 text-xs hover:bg-ae-blue-50 p-1 rounded"
                        >
                            <FontAwesomeIcon icon={faChevronsRight} />
                        </button>
                    </div>
                </div>
                <DateRangeField
                    valueAfter={startDateFilter}
                    valueBefore={endDateFilter}
                    onChangeAfter={handleChangeStartDate}
                    onChangeBefore={handleChangeEndDate}
                    showPresets={false}
                    showRangeNavigation={false}
                />
            </div>
        </ControlContainer>
    );
};
